import { Alert, Box, Button, Flashbar, Header, SpaceBetween, Table, TextFilter } from "@cloudscape-design/components";
import { QbilonService } from "../../services/qbilon-service";
import React from "react";
import { useState, useMemo } from "react";
import CreateRelationshipModal from "./create-relation-modal";
import DeleteRelationshipModal from "./delete-relation-modal";

export default function ProviderCapabilityTablePage() {

    const [edges, setEdges] = useState([]);
    const [providerNodes, setProviderNodes] = useState([]);
    const [serviceNodes, setServiceNodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null)
    const [
        selectedItems,
        setSelectedItems
    ] = React.useState([]);

    const [
        filteringText,
        setFilteringText
    ] = React.useState("");

    const [flashItems, setFlashItems] = useState([]);


    const [modalOpen, setModalOpen] = useState(false);
    const openModal = () => setModalOpen(true);
    const closeModal = () => setModalOpen(false);

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const openDeleteModal = () => setDeleteModalOpen(true);
    const closeDeleteModal = () => setDeleteModalOpen(false);

    const showFlashMessage = (message, type = "success") => {
        const id = Date.now().toString();
        setFlashItems([{
            type,
            content: message,
            dismissible: true,
            dismissLabel: "Schließen",
            onDismiss: () => setFlashItems(items => items.filter(item => item.id !== id)),
            id
        }]);
    };

    // Memoized lookup maps for O(1) performance
    const providerMap = useMemo(() =>
        new Map(providerNodes.map(n => [n.qbilonId, n.name])),
        [providerNodes]
    );

    const serviceMap = useMemo(() =>
        new Map(serviceNodes.map(n => [n.qbilonId, n.name])),
        [serviceNodes]
    );

    // Memoized filtered items to avoid duplicate calculations
    const filteredItems = useMemo(() =>
        edges.filter(item =>
            Object.values(item).some(value =>
                String(value).toLowerCase().includes(filteringText.toLowerCase())
            )
        ),
        [edges, filteringText]
    );

    React.useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                setError(null);

                const [data, providerData, serviceData] = await Promise.all([
                    QbilonService.getAllProvidesEdges(),
                    QbilonService.getAllProviderNodes(),
                    QbilonService.getAllServiceNodes()
                ]);

                setEdges(data);
                setProviderNodes(providerData);
                setServiceNodes(serviceData);
            } catch (e) {
                setError(e?.message ?? "Unbekannter Fehler beim Laden der Daten");
                console.error("Error fetching Data:", e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <>
            <Flashbar items={flashItems} />

            {error && (
                <Alert
                    type="error"
                    dismissible
                    onDismiss={() => setError(null)}
                    header="Fehler beim Laden"
                >
                    {error}
                </Alert>
            )}

            <CreateRelationshipModal
                onOpenSettings={modalOpen}
                onDismiss={closeModal}
                onSave={async () => {
                    const [data] = await Promise.all([
                        QbilonService.getAllProvidesEdges()
                    ]);
                    setEdges(data);
                    showFlashMessage("Beziehung erfolgreich erstellt");
                    closeModal();
                }}
                providerNodes={providerNodes}
                serviceNodes={serviceNodes}
            />

            <DeleteRelationshipModal
                onOpenSettings={deleteModalOpen}
                onDismiss={closeDeleteModal}
                onSave={async () => {
                    const [data] = await Promise.all([
                        QbilonService.getAllProvidesEdges()
                    ]);
                    setEdges(data);
                    showFlashMessage("Beziehung erfolgreich gelöscht");
                    closeDeleteModal();
                }}
                selectedItems={selectedItems}
            />

            <Table
                renderAriaLive={({
                    firstIndex,
                    lastIndex,
                    totalItemsCount
                }) =>
                    `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
                }
                onSelectionChange={({ detail }) =>
                    setSelectedItems(detail.selectedItems)
                }
                selectedItems={selectedItems}
                stripedRows
                loading={loading}
                ariaLabels={{
                    selectionGroupLabel: "Items selection",
                    allItemsSelectionLabel: () => "select all",
                    itemSelectionLabel: ({ selectedItems }, item) =>
                        item.id
                }}
                columnDefinitions={[
                    {
                        id: "id",
                        header: "ID",
                        cell: e => e.id,
                        isRowHeader: true
                    },
                    {
                        id: "provider",
                        header: "Provider",
                        cell: e => providerMap.get(e.provider) || e.provider,
                    },
                    {
                        id: "tso",
                        header: "Technical Service Offering",
                        cell: e => serviceMap.get(e.tso) || e.tso,
                    },
                    {
                        id: "type",
                        header: "Typ",
                        cell: e => e.type
                    },
                    {
                        id: "created",
                        header: "Erstellt am",
                        cell: e => e.created?.toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        }) || '—'
                    }
                ]}
                columnDisplay={[
                    { id: "id", visible: false },
                    { id: "provider", visible: true },
                    { id: "tso", visible: true },
                    { id: "type", visible: true },
                    { id: "created", visible: true }
                ]}
                enableKeyboardNavigation
                items={filteredItems}
                loadingText="Beziehungen laden..."
                selectionType="single"
                trackBy="id"
                empty={
                    <Box
                        margin={{ vertical: "xs" }}
                        textAlign="center"
                        color="inherit"
                    >
                        <SpaceBetween size="m">
                            <b>Keine Beziehungen vorhanden</b>
                            <Button onClick={openModal}>Neue Beziehung anlegen</Button>
                        </SpaceBetween>
                    </Box>
                }
                filter={
                    <TextFilter
                        filteringPlaceholder="Beziehungen suchen"
                        filteringText={filteringText}
                        onChange={({ detail }) =>
                            setFilteringText(detail.filteringText)
                        }
                        countText={filteredItems.length + " Ergebnisse"}
                    />
                }
                header={
                    <Header
                        counter={
                            "(" + edges.length + ")"
                        }
                        actions={
                            <SpaceBetween
                                direction="horizontal"
                                size="xs"
                            >
                                <Button onClick={openDeleteModal} disabled={selectedItems.length === 0}>Beziehung löschen</Button>
                                <Button variant="primary" onClick={openModal} disabled={loading}>Neue Beziehung anlegen</Button>
                            </SpaceBetween>
                        }
                    >
                        Provider Capabilities
                    </Header>
                }
            />
        </>

    )
}
